import React from "react";
import { Carousel, ContentCard, ContentCardSkeleton } from "~/components";
import { usePassionsData } from "~/hooks/usePassionsData";
import { getAnalyticsClickTrackingAttributes } from "~/utils/adobeDataLayerUtils";
import offersData from "~/data/offersData.json";

export const OffersCarousel = () => {
  const { getSelectedPassions, loading, error } = usePassionsData();

  // Get the user's selected passions
  const selectedPassions = getSelectedPassions();

  // If loading, show skeleton cards
  if (loading) {
    return (
      <div style={{}} data-testid="offers-carousel">
        <Carousel
          title="Offers & Promos Picked for You "
          showNavigation={true}
          gap={32}
        >
          {Array.from({ length: 6 }).map((_, index) => (
            <ContentCardSkeleton key={`skeleton-${index}`} />
          ))}
        </Carousel>
      </div>
    );
  }

  // If error, show error state but fallback to default content
  if (error) {
    console.warn("Error loading passions, showing default offers:", error);
  }

  // Default passions for fallback when no passions are selected
  const defaultPassions = ["golf", "beach", "culinary"];

  // Shuffle array function for randomization
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Get 6 offer cards total based on user's passions
  const getOfferCards = () => {
    const cards = [];

    // Logic based on number of selected passions
    if (selectedPassions.length === 0) {
      // No passions selected: 1 curated offer for each of the default passions = 5 cards
      defaultPassions.forEach((passion) => {
        if (offersData[passion] && offersData[passion].offers) {
          const passionOffers = offersData[passion].offers;
          if (passionOffers.length > 0) {
            cards.push({
              ...passionOffers[0], // Take first offer
              passion: passion,
              passionTitle: passion.charAt(0).toUpperCase() + passion.slice(1),
            });
          }
        }
      });
    } else if (selectedPassions.length === 1) {
      // One passion: 2 curated offers for the passion, 1 new offer for new passion
      const selectedPassion = selectedPassions[0];
      if (offersData[selectedPassion] && offersData[selectedPassion].offers) {
        const passionOffers = offersData[selectedPassion].offers;
        // Add up to 2 offers from the selected passion
        passionOffers.slice(0, 2).forEach((offer) => {
          cards.push({
            ...offer,
            passion: selectedPassion,
            passionTitle:
              selectedPassion.charAt(0).toUpperCase() +
              selectedPassion.slice(1),
          });
        });
      }

      // Add 1 offer from a new passion (not in selected passions)
      const availablePassions = Object.keys(offersData).filter(
        (passion) => !selectedPassions.includes(passion),
      );
      if (availablePassions.length > 0) {
        const randomNewPassion =
          availablePassions[
            Math.floor(Math.random() * availablePassions.length)
          ];
        if (
          offersData[randomNewPassion] &&
          offersData[randomNewPassion].offers
        ) {
          const newPassionOffers = offersData[randomNewPassion].offers;
          if (newPassionOffers.length > 0) {
            cards.push({
              ...newPassionOffers[0],
              passion: randomNewPassion,
              passionTitle:
                randomNewPassion.charAt(0).toUpperCase() +
                randomNewPassion.slice(1),
            });
          }
        }
      }
    } else if (selectedPassions.length >= 3) {
      // 3 or more passions: 2 curated offers for each of the guest's passion(s) = 6 offers
      selectedPassions.slice(0, 3).forEach((passion) => {
        if (offersData[passion] && offersData[passion].offers) {
          const passionOffers = offersData[passion].offers;
          // Add up to 2 offers from each passion
          passionOffers.slice(0, 2).forEach((offer) => {
            cards.push({
              ...offer,
              passion: passion,
              passionTitle: passion.charAt(0).toUpperCase() + passion.slice(1),
            });
          });
        }
      });
    }

    // If we don't have enough cards, fill with additional offers from available passions
    while (cards.length < 6 && Object.keys(offersData).length > 0) {
      const availablePassions = Object.keys(offersData);
      const randomPassion =
        availablePassions[Math.floor(Math.random() * availablePassions.length)];

      if (offersData[randomPassion] && offersData[randomPassion].offers) {
        const passionOffers = offersData[randomPassion].offers;
        const unusedOffers = passionOffers.filter(
          (offer) => !cards.some((card) => card.id === offer.id),
        );

        if (unusedOffers.length > 0) {
          const randomOffer =
            unusedOffers[Math.floor(Math.random() * unusedOffers.length)];
          cards.push({
            ...randomOffer,
            passion: randomPassion,
            passionTitle:
              randomPassion.charAt(0).toUpperCase() + randomPassion.slice(1),
          });
        } else {
          break; // No more unique offers available
        }
      }
    }

    // Randomize the order and return exactly 6 cards
    return shuffleArray(cards).slice(0, 6);
  };

  const offerCards = getOfferCards();

  const handleOfferClick = (offer) => {
    console.log("Clicked offer:", offer);
    // Open the button link in a new tab
    if (offer.buttonLink) {
      window.open(offer.buttonLink, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div style={{}} data-testid="offers-carousel">
      {/* Passion-based offer cards */}
      {offerCards.length > 0 && (
        <Carousel
          title="Offers & Promos Picked for You"
          showNavigation={true}
          gap={32}
          titleHref={"https://www.marriott.com/offers.mi"}
        >
          {offerCards.map((offer) => (
            <ContentCard
              key={offer.id}
              title={offer.title}
              description={offer.description}
              image={offer.image}
              activities={offer.activities || []}
              hotelLink={{ url: offer.buttonLink }}
              onClick={() => handleOfferClick(offer)}
              {...getAnalyticsClickTrackingAttributes(
                "Offers Carousel",
                "click",
                `Opened ${offer.title}`,
              )}
              // Add offer-specific styling or badges
              className="offer-card"
            />
          ))}
        </Carousel>
      )}

      {/* Fallback if no offer data is available */}
      {offerCards.length === 0 && (
        <div style={{ textAlign: "center" }}>
          <p>No personalized offers available at the moment.</p>
          <p>Complete your passion profile to see curated offers!</p>
        </div>
      )}
    </div>
  );
};
